import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

// Lo que se escribe en la pregunta se acumula y se aplica junto para no
// reescribir `pages` en cada tecla.
const WRITE_DELAY = 400;

// Ids que pertenecen a la pregunta misma. En los tipos con opciones, cualquier
// otro id que llegue a `handleOnChange` es el título de una opción.
export const QUESTION_FIELDS = [
  'title',
  'shortQuestion',
  'description',
  'imageKey',
  'sendEmail',
  'questionEmails',
];

const sameList = (a, b) => (
  Array.isArray(a) && Array.isArray(b)
  && a.length === b.length
  && a.every((item, index) => item === b[index])
);

/**
 * Lee la lista de emails de respaldo de una pregunta.
 *
 * Llega con dos formas: `[{ emails: 'a@x.com,b@x.com' }]` cuando viene del API y
 * `['a@x.com']` cuando la editó la UI en esta misma sesión.
 *
 * @param {object} source - Pregunta (`info`) o página en edición
 * @returns {?string[]}
 */
const readSavedEmails = (source) => {
  const list = source.QuestionEmails || source.questionEmails;
  const head = Array.isArray(list) ? list[0] : null;

  if (!head) return null;

  const emails = typeof head === 'string' ? list.join(',') : head.emails;

  return emails ? emails.split(',') : null;
};

/**
 * Estado y escritura compartidos por todos los tipos de pregunta
 * (`components/Pages/<Tipo>`). Devuelve el estado del formulario, los handlers
 * por defecto y las primitivas (`scheduleWrite`, `writePage`, `questionLogic`)
 * para que los tipos con lógica propia armen su `handleOnChange` sin duplicar la
 * plomería.
 *
 * Los cambios no se escriben de inmediato: se acumulan por campo y se aplican
 * todos juntos pasados `WRITE_DELAY` ms, o antes si el campo pierde el foco.
 * Acumularlos importa: con un `setTimeout` por cambio, editar dos campos
 * seguidos descartaba el primero.
 *
 * Opciones:
 * - `writeVia`: cómo se persiste un cambio. `'pages'` muta `pages[identifier - 1]`
 *   (no re-renderiza); `'setPages'` usa el setter del padre y, si no llega
 *   `setPages`, no escribe. Se mantiene la distinción porque las vistas de
 *   creación/edición sólo le pasan `setPages` a algunos tipos.
 * - `blurVia`: idem para el flush al perder el foco. Por defecto sigue a `writeVia`.
 * - `autoLoadMedia`: carga imagen y emails al montar. Los tipos que además
 *   cargan opciones o KPIs lo apagan y llaman `loadMedia` desde su propio efecto.
 */
export default function useQuestionForm({
  identifier,
  pages,
  info,
  setPages,
  writeVia = 'pages',
  blurVia = writeVia,
  autoLoadMedia = true,
}) {
  const page = pages?.[identifier - 1];

  const [open, setOpen] = useState(false);
  const [openEmails, setOpenEmails] = useState(false);
  const [sendEmail, setSendEmail] = useState(false);
  const [emailsArray, setEmailsArray] = useState([]);
  const [file, setFile] = useState({});
  const [image, setImage] = useState({});
  const [description, setDescription] = useState(
    info ? info.description : page?.description,
  );
  const [localTitle, setLocalTitle] = useState(
    info ? info.title : page?.title || '',
  );
  const [localShortQuestion, setLocalShortQuestion] = useState(
    info ? info.shortQuestion || '' : page?.shortQuestion || '',
  );
  const timeoutRef = useRef(null);
  const pendingRef = useRef(null);

  const questionLogic = useMemo(() => [{
    questionIdentifier: identifier,
    questionChoiceIdentifier: null,
    nextQuestionIdentifier: `${Number.parseInt(identifier, 10) + 1}`,
  }], [identifier]);

  const applyChanges = useCallback((changes, via) => {
    if (via === 'setPages') {
      if (!setPages) return;
      setPages((prevPages) => {
        const newPages = [...prevPages];
        newPages[identifier - 1] = { ...newPages[identifier - 1], ...changes };
        return newPages;
      });
      return;
    }
    if (!pages) return;
    // eslint-disable-next-line no-param-reassign
    pages[identifier - 1] = { ...pages[identifier - 1], ...changes };
  }, [identifier, pages, setPages]);

  const writePage = useCallback((changes) => {
    applyChanges(changes, writeVia);
  }, [applyChanges, writeVia]);

  const handleCategoryChange = useCallback((categoryId) => {
    writePage({ categoryId });
  }, [writePage]);

  /**
   * Aplica lo que haya quedado pendiente. Se llama al vencer el temporizador y
   * también al perder el foco, así hacer clic en Guardar justo después de tipear
   * no descarta la última edición.
   */
  const flushPendingWrites = useCallback((via) => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }

    const changes = pendingRef.current;
    pendingRef.current = null;

    if (changes) applyChanges(changes, via);
  }, [applyChanges]);

  const scheduleWrite = useCallback((changes) => {
    pendingRef.current = { ...pendingRef.current, ...changes };

    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    timeoutRef.current = setTimeout(() => flushPendingWrites(writeVia), WRITE_DELAY);
  }, [flushPendingWrites, writeVia]);

  const syncLocalField = useCallback((id, value) => {
    if (id === 'title') setLocalTitle(value);
    if (id === 'shortQuestion') setLocalShortQuestion(value);
  }, []);

  /**
   * Carga la imagen de referencia y los emails de respaldo desde la pregunta
   * (`info`) o desde la página en edición.
   *
   * Los setters son idempotentes a propósito: hay tipos que llaman a `loadMedia`
   * desde un efecto que depende de `pages` o de `image`, y devolver estado nuevo
   * sin que haya cambiado nada los dejaría en loop.
   */
  const loadMedia = useCallback((source) => {
    if (!source) return;

    setImage((prevImage) => (
      prevImage.url === source.imageUrl && prevImage.name === source.imageKey
        ? prevImage
        : { ...prevImage, url: source.imageUrl, name: source.imageKey }
    ));

    const emails = readSavedEmails(source);

    if (emails) {
      setEmailsArray((prevEmails) => (sameList(prevEmails, emails) ? prevEmails : emails));
    }

    setSendEmail(!!source.sendEmail);
  }, []);

  const handleOnChange = useCallback((event) => {
    const { id, value, imageUrl } = event.target;

    syncLocalField(id, value);

    scheduleWrite({
      [id]: value,
      questionChoices: null,
      questionLogic,
      imageUrl,
    });
  }, [questionLogic, scheduleWrite, syncLocalField]);

  const handleBlur = useCallback(() => {
    if (localTitle !== undefined) {
      pendingRef.current = { ...pendingRef.current, title: localTitle };
    }
    flushPendingWrites(blurVia);
  }, [blurVia, flushPendingWrites, localTitle]);

  useEffect(() => {
    if (!autoLoadMedia) return;
    loadMedia(info || page);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setLocalTitle(info ? info.title : pages?.[identifier - 1]?.title || '');
    setLocalShortQuestion(
      info ? info.shortQuestion || '' : pages?.[identifier - 1]?.shortQuestion || '',
    );
  }, [info, pages, identifier]);

  // Si la pregunta se desmonta con cambios pendientes (por ejemplo al eliminar
  // otra y renumerarse la lista), se aplican antes de irse. Va por un ref para
  // que el cleanup corra sólo al desmontar y no en cada re-render del padre.
  const applyChangesRef = useRef(applyChanges);
  applyChangesRef.current = applyChanges;

  useEffect(() => () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    if (pendingRef.current) {
      applyChangesRef.current(pendingRef.current, 'pages');
      pendingRef.current = null;
    }
  }, []);

  return {
    // estado
    open,
    setOpen,
    openEmails,
    setOpenEmails,
    sendEmail,
    setSendEmail,
    emailsArray,
    setEmailsArray,
    file,
    setFile,
    image,
    setImage,
    description,
    setDescription,
    localTitle,
    setLocalTitle,
    localShortQuestion,
    setLocalShortQuestion,
    categoryId: info?.categoryId ?? page?.categoryId ?? null,
    handleCategoryChange,
    // handlers por defecto
    handleOnChange,
    handleBlur,
    // primitivas para los tipos con lógica propia
    scheduleWrite,
    writePage,
    questionLogic,
    syncLocalField,
    loadMedia,
  };
}
